import { computed, type ComputedRef } from 'vue'
import { markdownToText } from '~/utils/markdown'

interface FaqQuestion {
  question: string
  answer: string
}

interface FaqBlock {
  __component: string
  questions?: FaqQuestion[]
}

interface FaqStructuredData {
  "@context": string
  "@type": string
  "mainEntity": Array<{
    "@type": string
    "name": string
    "acceptedAnswer": {
      "@type": string
      "text": string
    }
  }>
}

/**
 * Composable for generating FAQ structured data according to Google's guidelines
 * Combines all FAQ blocks on a page into a single FAQPage structured data
 * 
 * @param blocks - Array of content blocks to scan for FAQ blocks
 * @returns FAQ structured data object or null if no FAQs found
 */
export const useFaqStructuredData = (blocks: ComputedRef<any[]> | any[]): ComputedRef<FaqStructuredData | null> => {
  return computed(() => {
    const blocksArray = Array.isArray(blocks) ? blocks : blocks.value || []
    const faqBlocks = blocksArray.filter((block: FaqBlock) => block.__component === 'blocks.faq')
    
    if (faqBlocks.length === 0) {
      return null
    }

    // Collect all questions from all FAQ blocks
    const allQuestions: FaqStructuredData["mainEntity"] = []
    
    faqBlocks.forEach((block: FaqBlock) => {
      if (block.questions && Array.isArray(block.questions)) {
        block.questions.forEach((faq: FaqQuestion) => {
          if (faq.question && faq.answer) {
            allQuestions.push({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": markdownToText(faq.answer)
              }
            })
          }
        })
      }
    })

    // Only generate structured data if we have questions
    if (allQuestions.length === 0) {
      return null
    }

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": allQuestions
    }
  })
} 